/**
 * @file HaulageJobModal.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-23
 * @modified 2026-09-30
 *
 * Viewing a haulage job.
 *
 * There was no view for one at all: the only way into a job was the edit
 * modal, which meant opening a form to read a record — every field a live
 * input, with a Save button sitting under a glance. This is the read, shaped
 * like the customer order modal so the two feel like the same system: who,
 * where, what, when, what it costs, and what has happened to it.
 *
 * Comprehensive on purpose. A view that shows six of a record's fifteen fields
 * sends people to the edit form for the other nine, which is how the edit form
 * became the view in the first place. Everything the job carries is here,
 * including the transport planning fields that only the planning board writes.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import ContactModal from './ContactModal';
import AuditHistoryModal from './AuditHistoryModal';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import './HaulageJobModal.css';
import FindInSearchButton from './FindInSearchButton';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** One label/value pair. Never renders an empty string as a value. */
function Field({ label, children, wide }) {
  return (
    <div className={`hj-field${wide ? ' wide' : ''}`}>
      <span className="hj-label">{label}</span>
      <span className="hj-value">{children ?? '—'}</span>
    </div>
  );
}

/**
 * The footer's slide-out buttons must be measured in the render cycle they
 * mount in, and Modal portals its content a cycle after the parent renders —
 * so the hook runs here rather than in HaulageJobModal.
 */
function ModalFooter({ job, onEdit, onClose, onHistory }) {
  useSlideOutButton([job?.id]);
  return (
    <div className="buttons slideout">
      <div>
        <button data-read-safe className="btn-slideout" onClick={onHistory}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">history</span></div>
          <span className="btn-label">History</span>
        </button>
      </div>
      <FindInSearchButton type="haulage_job" record={job} onClose={onClose} wrap />
      {onEdit && (
        <div>
          <button data-write-action className="btn-slideout" onClick={() => onEdit(job)}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
            <span className="btn-label">Edit</span>
          </button>
        </div>
      )}
      <div>
        <button data-read-safe className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
    </div>
  );
}

/**
 * @param {number|null} jobId   The haulage job to show; null closes.
 * @param {function} onClose
 * @param {function} [onEdit]   Omitted for a caller with no edit route.
 */
function HaulageJobModal({ jobId, onClose, onEdit }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/haulage-jobs/${jobId}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not load the haulage job.');
        return data;
      })
      .then((data) => { if (!cancelled) setJob(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  if (!jobId) return null;

  // A job either goes to a contact on file or to a one-off typed in at
  // creation. Both are shown the same way; only the first is clickable.
  const manual = job?.manual_entry || null;
  const deliveryName = job?.customer_name || manual?.name || '—';
  const deliveryAddress = [
    manual?.address_line1, manual?.address_line2, manual?.city, manual?.postcode
  ].filter(Boolean).join(', ');

  const contactLink = (id, name) => (id
    ? <span className="contact-link" onClick={() => setContactId(id)}>{name}</span>
    : <span>{name}</span>);

  const moffett = Number(job?.moffett_charge) > 0 ? Number(job.moffett_charge) : 0;
  const rate = job?.rate != null ? Number(job.rate) : null;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        className="haulage-job"
        title={job ? `Haulage Job ${job.job_number}` : 'Haulage Job'}
        footer={job ? (
          <ModalFooter
            job={job}
            onEdit={onEdit}
            onClose={onClose}
            onHistory={() => setShowHistory(true)}
          />
        ) : null}
      >
        {loading && <LoadingSpinner />}
        {error && <p className="hj-error">{error}</p>}

        {job && !loading && (
          <>
            {/* Status first: it is the question most often being asked of a
                job somebody has just opened. */}
            <section className="hj-section">
              <h3>Status</h3>
              <div className="hj-grid">
                <Field label="Delivery">{job.delivery_status || '—'}</Field>
                <Field label="Invoiced">
                  {job.invoiced_status === 'Invoiced'
                    ? `Invoiced${job.invoice_number ? ` (${job.invoice_number})` : ''}`
                    : 'Not invoiced'}
                </Field>
                <Field label="Archived">{job.is_archived ? 'Yes' : 'No'}</Field>
              </div>
            </section>

            <section className="hj-section">
              <h3>Haulier &amp; Delivery</h3>
              <div className="hj-grid">
                <Field label="Supplier">
                  {contactLink(job.supplier_account_id, job.supplier_name || '—')}
                </Field>
                <Field label="Delivery customer">
                  {contactLink(job.customer_account_id, deliveryName)}
                </Field>
                {deliveryAddress && <Field label="Delivery address" wide>{deliveryAddress}</Field>}
                <Field label="Created">{formatDate(job.created_at)}</Field>
                <Field label="Delivery date">{formatDate(job.delivery_date)}</Field>
                {job.delivery_instructions && (
                  <Field label="Delivery instructions" wide>{job.delivery_instructions}</Field>
                )}
              </div>
            </section>

            <section className="hj-section">
              <h3>Load</h3>
              <div className="hj-grid">
                <Field label="Product">{job.product_details || '—'}</Field>
                <Field label="Quantity">
                  {job.quantity != null ? formatQuantity(job.quantity) : '—'}
                </Field>
                <Field label="Units">{job.units || '—'}</Field>
              </div>
            </section>

            <section className="hj-section">
              <h3>Charges</h3>
              <div className="hj-grid">
                <Field label="Rate">{rate != null ? formatCurrency(rate) : '—'}</Field>
                <Field label="Rate band">
                  {job.rate_postcode_prefix
                    ? `Postcode ${job.rate_postcode_prefix}`
                    : (rate != null ? 'Supplier default' : '—')}
                </Field>
                {/* A dash, not £0.00: the Moffett charge is optional and a zero
                    reads as one that was made. */}
                <Field label="Moffett">{moffett > 0 ? formatCurrency(moffett) : '—'}</Field>
                <Field label="Total">
                  {rate != null || moffett > 0 ? formatCurrency((rate || 0) + moffett) : '—'}
                </Field>
              </div>
            </section>

            {/* Written by the planning board rather than by whoever raised the
                job, so it is its own section — blank here means not yet
                planned, not missing. */}
            {(job.haulier || job.driver || job.vehicle || job.scheduled_time
              || job.sequence != null || job.door_config) && (
              <section className="hj-section">
                <h3>Transport Planning</h3>
                <div className="hj-grid">
                  <Field label="Haulier">{job.haulier || '—'}</Field>
                  <Field label="Driver">{job.driver || '—'}</Field>
                  <Field label="Vehicle">{job.vehicle || '—'}</Field>
                  <Field label="Scheduled">{job.scheduled_time || '—'}</Field>
                  <Field label="Drop sequence">{job.sequence ?? '—'}</Field>
                  <Field label="Compartments">{job.door_config || '—'}</Field>
                </div>
              </section>
            )}
          </>
        )}
      </Modal>

      {contactId && (
        <ContactModal contactId={contactId} onClose={() => setContactId(null)} />
      )}

      {job && (
        <AuditHistoryModal
          isOpen={showHistory}
          entityType="haulage_job"
          entityId={job.id}
          entityIdentifier={job.job_number}
          onClose={() => setShowHistory(false)}
        />
      )}
    </>
  );
}

export default HaulageJobModal;
